import React, { useState } from 'react';

import Icon from 'components/DesignSystem/Icon';
import { SortDirection } from 'constants/sorting';
import { IndiceClimatico, IndiceClimaticoField } from 'types/Meteorology/indicesClimaticos';
import { SimilarityIndexFilters } from 'hooks/Meteorology/Agro/Similarity/Mobile/useSimilarityMobile';
import { cssVar } from 'constants/theme';
import YearCard from '../YearCard';
import FiltersSheet from '../FiltersSheet';
import SortSheet, { SortField } from '../SortSheet';
import INDICES from '../indices';

import './index.scss';

export interface IndicesSectionProps {
  isLoading: boolean;
  isError: boolean;
  rows: IndiceClimatico[];
  mapsGenerated: boolean;
  excludedYears: Set<string>;
  onToggleMap: (ano: number, mes: number) => void;
  filters: SimilarityIndexFilters;
  toggleFilter: (indexId: IndiceClimaticoField, label: string) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  sortField: SortField;
  sortDirection: SortDirection;
  onChangeSort: (field: SortField, direction: SortDirection) => void;
}

export default function IndicesSection({
  isLoading,
  isError,
  rows,
  mapsGenerated,
  excludedYears,
  onToggleMap,
  filters,
  toggleFilter,
  clearFilters,
  activeFilterCount,
  sortField,
  sortDirection,
  onChangeSort,
}: IndicesSectionProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const rowsCountLabel = `${rows.length} ano${rows.length !== 1 ? 's' : ''}`;
  const sortLabel = sortField === 'ano' ? 'Ano' : INDICES.find(({ id }) => id === sortField)?.abbr;

  return (
    <div className="agro-simil-indices-section">
      <div className="agro-simil-indices-section-header">
        <span className="agro-simil-indices-section-title">Índices Climáticos</span>
        <span className="agro-simil-indices-section-count">{rowsCountLabel}</span>
      </div>

      <div className="agro-simil-indices-section-toolbar">
        <button
          type="button"
          className="agro-simil-indices-section-toolbar-filters"
          onClick={() => setFiltersOpen(true)}
        >
          <Icon iconType="filter" color={cssVar('text-on-accent')} size="small" />
          Filtros
          {activeFilterCount > 0 && (
            <span className="agro-simil-indices-section-toolbar-badge">{activeFilterCount}</span>
          )}
        </button>
        <button
          type="button"
          className="agro-simil-indices-section-toolbar-sort"
          onClick={() => setSortOpen(true)}
        >
          <Icon
            iconType={sortDirection === 'ASC' ? 'sort_ascending' : 'sort_descending'}
            color={cssVar('text-secondary')}
            size="small"
          />
          {sortLabel}
        </button>
        <button
          type="button"
          className="agro-simil-indices-section-toolbar-clear"
          onClick={clearFilters}
          aria-label="Limpar filtros"
        >
          <Icon iconType="close" color={cssVar('text-muted')} size="small" />
        </button>
      </div>

      {!isLoading && !isError && rows.length === 0 && (
        <div className="agro-simil-indices-section-empty">
          <div className="agro-simil-indices-section-empty-title">Nenhum dado encontrado</div>
          <div className="agro-simil-indices-section-empty-text">
            Nenhum ano corresponde às fases de índice filtradas. Ajuste os filtros.
          </div>
        </div>
      )}

      {isError && (
        <div className="agro-simil-indices-section-empty">
          <div className="agro-simil-indices-section-empty-title">
            Erro ao carregar dados dos índices climáticos
          </div>
        </div>
      )}

      {!isError && rows.length > 0 && (
        <div className="agro-simil-indices-section-list">
          {rows.map((row) => (
            <YearCard
              key={`${row.ano}-${row.mes}`}
              row={row}
              hasMap={mapsGenerated}
              active={!excludedYears.has(`${row.ano}-${row.mes}`)}
              onToggle={() => onToggleMap(row.ano, row.mes)}
            />
          ))}
        </div>
      )}

      <FiltersSheet
        open={filtersOpen}
        dismiss={() => setFiltersOpen(false)}
        filters={filters}
        onToggle={toggleFilter}
        onClear={clearFilters}
        resultCount={rows.length}
      />

      <SortSheet
        open={sortOpen}
        dismiss={() => setSortOpen(false)}
        sortField={sortField}
        sortDirection={sortDirection}
        onChange={onChangeSort}
      />
    </div>
  );
}
